import { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardService } from '../services';
import { getApiErrorMessage } from '../lib/apiError';

const POLL_MS = 5000;

/** A message for the page's alert. 'info' while a run is going, then success or error. */
export interface RunResult {
  message: string;
  severity: 'info' | 'success' | 'error';
}

/**
 * The dashboard's manual "Sync DHIS2" and "Schedule Reminders" runs: trigger,
 * poll until the server says done, then call onDone so the page can reload.
 */
export function useManualRuns(onDone: () => void) {
  const [syncing, setSyncing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const timers = useRef<number[]>([]);
  // False once the page has gone, so a status reply that lands late starts no new polling.
  const alive = useRef(true);

  // Polls a status check until it reports idle. Called after a trigger, and on
  // mount when a run started elsewhere is still going.
  const pollUntilDone = useCallback(
    (isRunning: () => Promise<boolean>, setRunning: (v: boolean) => void, doneMessage: string | null) => {
      if (!alive.current) return;
      const id = window.setInterval(async () => {
        try {
          if (await isRunning()) return;
          window.clearInterval(id);
          setRunning(false);
          if (doneMessage) setResult({ message: doneMessage, severity: 'success' });
          onDoneRef.current();
        } catch (err) {
          window.clearInterval(id);
          setRunning(false);
          setResult({
            message: `Could not check progress: ${getApiErrorMessage(err)}. Refresh the page to see the latest.`,
            severity: 'error',
          });
        }
      }, POLL_MS);
      timers.current.push(id);
    },
    []
  );

  const syncRunning = async () => !!(await dashboardService.getSyncStatus()).syncing;
  const scheduleRunning = async () => !!(await dashboardService.getScheduleStatus()).scheduling;

  // A run may already be going from another tab; keep the buttons disabled so
  // the admin cannot fire a duplicate. No "completed" message, since they did not start it.
  useEffect(() => {
    alive.current = true;
    // Per mount, not the shared ref: React's dev double mount would otherwise let the
    // first mount's reply start a second poller.
    let cancelled = false;
    // A failed check just means we cannot see a run started elsewhere; the buttons still work.
    Promise.all([
      dashboardService.getSyncStatus().catch(() => null),
      dashboardService.getScheduleStatus().catch(() => null),
    ]).then(([syncStatus, scheduleStatus]) => {
      if (cancelled) return;
      if (syncStatus?.syncing) {
        setSyncing(true);
        pollUntilDone(syncRunning, setSyncing, null);
      }
      if (scheduleStatus?.scheduling) {
        setScheduling(true);
        pollUntilDone(scheduleRunning, setScheduling, null);
      }
    });
    const active = timers.current;
    return () => {
      cancelled = true;
      alive.current = false;
      active.forEach((id) => window.clearInterval(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSync = async () => {
    try {
      setSyncing(true);
      setResult(null);
      await dashboardService.triggerSync();
      setResult({ message: 'DHIS2 sync started. This may take a few minutes...', severity: 'info' });
      pollUntilDone(syncRunning, setSyncing, 'DHIS2 sync completed successfully');
    } catch (err) {
      setResult({ message: `Sync failed: ${getApiErrorMessage(err)}`, severity: 'error' });
      setSyncing(false);
    }
  };

  const runSchedule = async () => {
    try {
      setScheduling(true);
      setResult(null);
      await dashboardService.scheduleReminders();
      setResult({ message: 'Scheduling reminders for upcoming visits...', severity: 'info' });
      pollUntilDone(scheduleRunning, setScheduling, 'Reminder scheduling completed');
    } catch (err) {
      setResult({ message: `Failed: ${getApiErrorMessage(err)}`, severity: 'error' });
      setScheduling(false);
    }
  };

  return { syncing, scheduling, result, clearResult: () => setResult(null), runSync, runSchedule };
}
